import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  PushpinFilled,
  PushpinOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import { type MouseEvent, type ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  HighlightOverlay,
  IconAction,
  OptionActions,
  OptionLabel,
  OptionRow,
} from './UserTemplateOptionItem.style';
import { type UserTemplateOptionFields, formatUserTemplateOptionLabel } from './utils';

type Props = {
  option: UserTemplateOptionFields;
  pinned: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  disabledReason?: string;
  highlighted?: boolean;
  highlightToken?: number;
  onPin: () => void;
  onUnpin: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
};

type TooltipActionIconProps = {
  title: string;
  icon: ReactNode;
  testId: string;
  className?: string;
  disabled?: boolean;
  onAction?: () => void;
};

const TooltipActionIcon = ({
  title,
  icon,
  testId,
  className,
  disabled = false,
  onAction,
}: TooltipActionIconProps) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const keepTooltipClosedRef = useRef(false);

  const handleClick = (event: MouseEvent) => {
    // Stopped even when disabled: rc-menu would otherwise read the click as
    // choosing the row.
    event.preventDefault();
    event.stopPropagation();
    if (disabled) {
      return;
    }
    keepTooltipClosedRef.current = true;
    setTooltipOpen(false);
    onAction?.();
  };

  return (
    <Tooltip
      placement="topLeft"
      title={title}
      open={tooltipOpen}
      destroyOnHidden
      onOpenChange={(open) => {
        setTooltipOpen(keepTooltipClosedRef.current ? false : open);
      }}
    >
      <IconAction
        className={className}
        data-testid={testId}
        data-disabled={disabled || undefined}
        $disabled={disabled}
        onClick={handleClick}
        onMouseLeave={() => {
          keepTooltipClosedRef.current = false;
          setTooltipOpen(false);
        }}
      >
        {icon}
      </IconAction>
    </Tooltip>
  );
};

export const UserTemplateOptionItem = ({
  option,
  pinned,
  isFirst = false,
  isLast = false,
  disabledReason,
  highlighted = false,
  highlightToken = 0,
  onPin,
  onUnpin,
  onMoveUp,
  onMoveDown,
}: Props) => {
  const { t } = useTranslation();
  const label = formatUserTemplateOptionLabel(t, option);

  // The label wraps, so the whole name is always on screen. A row that cannot run
  // says why on hover.
  const labelNode = <OptionLabel>{label}</OptionLabel>;

  return (
    <OptionRow>
      {highlighted ? (
        // Keyed by the action count: acting on the same row again remounts the
        // overlay, which is what restarts its animation.
        <HighlightOverlay
          key={highlightToken}
          aria-hidden
          data-testid="user-template-option-highlight-flash"
        />
      ) : null}
      {disabledReason ? <Tooltip title={disabledReason}>{labelNode}</Tooltip> : labelNode}
      {/* Pointer affordances only. Everything inside an rc-menu menuitem is
          presentational to assistive tech and unreachable by its roving tabindex, so
          these stay out of the accessibility tree and the tab order. The keyboard
          path is the row's own shortcuts, wired where the row is built. */}
      <OptionActions aria-hidden data-testid="user-template-option-actions">
        {pinned ? (
          <TooltipActionIcon
            title={t('reports.unpinReport', 'Unpin')}
            testId="user-template-option-unpin"
            className="user-template-option-pin"
            icon={<PushpinFilled />}
            onAction={onUnpin}
          />
        ) : (
          <TooltipActionIcon
            title={t('reports.pinReport', 'Pin')}
            testId="user-template-option-pin"
            className="user-template-option-pin"
            icon={<PushpinOutlined />}
            onAction={onPin}
          />
        )}
        {pinned ? (
          <>
            <TooltipActionIcon
              title={t('reports.movePinUp', 'Move up')}
              testId="user-template-option-move-up"
              icon={<ArrowUpOutlined />}
              disabled={isFirst}
              onAction={onMoveUp}
            />
            <TooltipActionIcon
              title={t('reports.movePinDown', 'Move down')}
              testId="user-template-option-move-down"
              icon={<ArrowDownOutlined />}
              disabled={isLast}
              onAction={onMoveDown}
            />
          </>
        ) : null}
      </OptionActions>
    </OptionRow>
  );
};
